import createDOMPurify from 'dompurify';

const purifier = createDOMPurify(window);
const allowedClasses = new Set(['image', 'image-inline', 'image-style-side', 'image-style-align-left', 'image-style-align-center', 'image-style-align-right', 'table']);

export const safeReviewUrl = (value, image = false) => {
  const url = typeof value === 'string' ? value.trim() : '';
  // Attribute entities are decoded by DOMPurify before this check.
  // eslint-disable-next-line no-control-regex
  if (!url || /[\u0000-\u0020\u007f\\<>"']/.test(url)) return '';
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  if (!image && url.startsWith('#')) return url;
  try {
    const parsed = new URL(url);
    if (parsed.username || parsed.password) return '';
    if (['http:', 'https:'].includes(parsed.protocol)) return url;
    if (!image && ['mailto:', 'tel:'].includes(parsed.protocol)) return url;
  } catch (_error) {
    return '';
  }
  return '';
};

const safeStyle = (style) => String(style || '').split(';').map((declaration) => {
  const [property, ...rest] = declaration.split(':');
  const key = property.trim().toLowerCase();
  const value = rest.join(':').trim().toLowerCase();
  if (key === 'text-align' && /^(left|center|right|justify)$/.test(value)) return `${key}:${value}`;
  if (['width', 'height', 'max-width', 'margin-left', 'margin-right'].includes(key) && /^(auto|0|\d{1,4}(\.\d{1,3})?(px|%|em|rem))$/.test(value)) return `${key}:${value}`;
  return '';
}).filter(Boolean).join(';');

purifier.addHook('uponSanitizeAttribute', (node, data) => {
  if (data.attrName === 'href' || data.attrName === 'src') {
    data.attrValue = safeReviewUrl(data.attrValue, data.attrName === 'src');
    if (!data.attrValue) data.keepAttr = false;
  }
  if (data.attrName === 'class') {
    data.attrValue = data.attrValue.split(/\s+/).filter((value) => allowedClasses.has(value)).join(' ');
  }
  if (data.attrName === 'style') data.attrValue = safeStyle(data.attrValue);
  if (data.attrName === 'target' && data.attrValue !== '_blank') data.keepAttr = false;
});
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') node.setAttribute('rel', 'nofollow noopener noreferrer');
  if (node.tagName === 'IMG') node.setAttribute('referrerpolicy', 'no-referrer');
});

export const sanitizeReviewHtml = (html) => {
  const raw = typeof html === 'string' ? html : '';
  if (!purifier.isSupported) {
    // Unsupported browsers must never receive raw rich text.
    return raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
  return purifier.sanitize(raw, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'blockquote', 'ul', 'ol', 'li', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'hr', 'a', 'img', 'figure', 'figcaption', 'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'pre', 'code'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel', 'width', 'height', 'class', 'style', 'start', 'colspan', 'rowspan', 'scope'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'svg', 'math', 'template'],
  });
};
